import type { MouseEvent } from "react";
import classNames from "classnames";
import { useQueryClient } from "react-query";
import invariant from "tiny-invariant";
import { t } from "@/i18n-js/instance";
import { getCommentsListKey } from "@/react/components/CommentsV3/CommentsList/getCommentsListKey";
import { getCommentHierarchyKey } from "@circle-react/components/CommentsV3/CommentHierarchy/useCommentHierarchy";
import { findMatchingQueryKeysForPostId } from "@circle-react/contexts/postsDetailsContext";
import { chatEventsEmitter } from "@circle-react/hooks/chatsV2";
import { useAnyHoverMediaQuery } from "@circle-react/hooks/useMediaQuery";
import type { Bookmark, CommentBookmark } from "@circle-react/types/Bookmarks";
import type { BookmarkType } from "@circle-react/types/Bookmarks";
import { useBookmarks } from "@circle-react-shared/Bookmarks/hooks/useBookmarks";
import { IconButton } from "@circle-react-shared/uikit/HeaderV3/IconButton";
import { Dropdown } from "@circle-react-uikit/Dropdown";

const isCommentBookmark = (bookmark: Bookmark): bookmark is CommentBookmark =>
  "post_id" in bookmark;

export interface ActionsProps {
  bookmarkId: number;
  bookmarkRecord: Bookmark | null;
  bookmarkType: BookmarkType;
  recordId: number | null;
}

export const Actions = ({
  bookmarkId,
  bookmarkType,
  bookmarkRecord,
  recordId,
}: ActionsProps) => {
  const canHover = useAnyHoverMediaQuery();
  const queryClient = useQueryClient();
  const refetchCommentList = getCommentsListKey;
  const refetchCommentHierarchy = getCommentHierarchyKey;

  const { resetQuery, deleteBookmark } = useBookmarks({
    bookmarkId,
    bookmarkType,
    onSuccess: () => {
      if (bookmarkType === "post" || bookmarkType === "event") {
        invariant(recordId, "Record ID is required");
        const matchingQueryKeys = findMatchingQueryKeysForPostId({
          postId: recordId,
          queryClient,
        });
        matchingQueryKeys.forEach((key: any) => {
          void queryClient.invalidateQueries(key);
        });
      }
      if (
        bookmarkType === "comment" &&
        bookmarkRecord &&
        isCommentBookmark(bookmarkRecord)
      ) {
        void queryClient.refetchQueries(
          refetchCommentList({
            postId: bookmarkRecord.post_id,
            parentCommentId: bookmarkRecord.parent_comment_id,
          }),
        );
        void queryClient.refetchQueries(
          refetchCommentHierarchy({
            postId: bookmarkRecord.post_id,
            id: bookmarkRecord.id,
          }),
        );
      }
      if (bookmarkType === "message" && recordId) {
        chatEventsEmitter.toggleMessageBookmark(recordId);
      }
      void resetQuery();
    },
  });
  return (
    <Dropdown
      size="medium"
      direction="bottom-end"
      className={classNames("size-6", {
        "invisible group-hover:visible": canHover,
      })}
      button={
        <IconButton
          name="16-menu-dots-horizontal"
          ariaLabel={t("options_dropdown")}
        />
      }
      transitionWrapperClassName="z-30"
    >
      <Dropdown.ItemLink
        onClick={(e: MouseEvent) => {
          deleteBookmark();
          e.stopPropagation();
        }}
      >
        {t("bookmarks.actions.remove")}
      </Dropdown.ItemLink>
    </Dropdown>
  );
};
